import * as z from 'zod';

/**
 * The 15 raw volunteer slot status strings as emitted by the upstream CRM.
 */
export enum VolunteerSlotStatusEnum {
  Available = 'Available',
  Approved = 'Approved',
  PendingApproval = 'Pending Approval',
  PendingApprovalFirst = 'Pending Approval-First Time',
  PendingApprovalGuest = 'Pending Approval Guest',
  CanceledAbandoned = 'Cancelled - Abandoned',
  CanceledByVolunteer = 'Cancelled - Volunteer Initiated',
  CanceledCOVIDConcern = 'Cancelled - COVID Concern',
  CanceledEvent = 'Cancelled - Event Cancelled',
  CanceledWithinFortyEightHours = 'Cancelled - Within 48 Hours',
  Completed = 'Completed',
  Confirmed = 'Confirmed',
  CompletedNoShow = 'Completed - No Show',
  Declined = 'Declined',
  Waitlist = 'Waitlist',
}

export interface VolunteerSlotStatusUpdateRequest {
  contactId: string;
  slotId: string;
  status: VolunteerSlotStatusEnum;
}

export interface VolunteerSlotStatusUpdateResponse {
  successfulUpdates: VolunteerSlotStatusUpdateRequest[];
  failedUpdates: VolunteerSlotStatusUpdateRequest[];
}

// ─── Volunteer Slots ─────────────────────────────────────────────────────────
// Shared slot shapes remain operation-agnostic for reuse.
export const VolunteerGuestSchema = z.object({
  firstName: z.string(),
  lastName: z.string(),
  email: z.string().email(),
  phone: z.string().nullable(),
  zipcode: z.string().nullable(),
  isMinor: z.boolean(),
  relationship: z.string(),
});

export const VolunteerContactSlotSchema = z.object({
  slotId: z.string(),
  contactId: z.string(),
  isWaiverComplete: z.boolean(),
});

export const VolunteerGuestSlotSchema = z.object({
  slotId: z.string(),
  guest: VolunteerGuestSchema,
  isWaiverComplete: z.boolean(),
});

export const VolunteerSlotSchema = z.union([
  VolunteerContactSlotSchema,
  VolunteerGuestSlotSchema,
]);

// ─── Volunteer Registration ──────────────────────────────────────────────────
// Internal callers send supporterId in the URL.
export const VolunteerRegistrationPayloadSchema = z.object({
  slots: z.array(VolunteerSlotSchema).min(1, 'At least one slot is required.'),
});

// The upstream API requires supporterId in the body.
export const VolunteerRegistrationUpstreamPayloadSchema =
  VolunteerRegistrationPayloadSchema.extend({
    supporterId: z.string(),
  });

// Types derived from the schemas.
export type VolunteerGuest = z.infer<typeof VolunteerGuestSchema>;
export type VolunteerContactSlot = z.infer<typeof VolunteerContactSlotSchema>;
export type VolunteerGuestSlot = z.infer<typeof VolunteerGuestSlotSchema>;
export type VolunteerSlot = z.infer<typeof VolunteerSlotSchema>;
export type VolunteerRegistrationPayload = z.infer<
  typeof VolunteerRegistrationPayloadSchema
>;
export type VolunteerRegistrationUpstreamPayload = z.infer<
  typeof VolunteerRegistrationUpstreamPayloadSchema
>;

// ─── Volunteer Registration Response ─────────────────────────────────────────
// One result per slot; new guest contacts return isNewContact + their contactId.
export const VolunteerRegistrationSlotResultSchema = z.object({
  slotId: z.string(),
  contactId: z.string(),
  isNewContact: z.boolean(),
});

/** Machine-readable codes for per-slot registration failures. */
export enum VolunteerRegistrationErrorCode {
  SlotNotFound = 'SLOT_NOT_FOUND',
  AlreadyBooked = 'ALREADY_BOOKED',
  SameDayConflict = 'SAME_DAY_CONFLICT',
  DuplicateInShift = 'DUPLICATE_IN_SHIFT',
  GuestUnresolved = 'GUEST_UNRESOLVED',
}

// One error per slot that failed; contactId is null when it can't be resolved.
export const VolunteerRegistrationSlotErrorSchema = z.object({
  slotId: z.string(),
  contactId: z.string().nullable(),
  code: z.string(),
  message: z.string(),
});

// Success envelope: per-slot results keyed under `results`.
export const VolunteerRegistrationResponseSchema = z.object({
  results: z.array(VolunteerRegistrationSlotResultSchema),
});

// Failure envelope: per-slot errors keyed under `errors`.
export const VolunteerRegistrationErrorResponseSchema = z.object({
  errors: z.array(VolunteerRegistrationSlotErrorSchema),
});

export type VolunteerRegistrationSlotResult = z.infer<
  typeof VolunteerRegistrationSlotResultSchema
>;

export type VolunteerRegistrationSlotError = z.infer<
  typeof VolunteerRegistrationSlotErrorSchema
>;

export type VolunteerRegistrationResponse = z.infer<
  typeof VolunteerRegistrationResponseSchema
>;

export type VolunteerRegistrationErrorResponse = z.infer<
  typeof VolunteerRegistrationErrorResponseSchema
>;

/** Client-facing result shape returned after a registration attempt. */
export interface VolunteerRegistrationResult {
  success: boolean;
  results?: VolunteerRegistrationSlotResult[];
  errors?: VolunteerRegistrationSlotError[];
  sessionExpired?: boolean;
}
